import emoji from "emoji-dictionary"
import CodeFormatter from "../components/CodeFormatter"
import CodeArticle from "../components/CodeArticle"
import { fs } from "../controls/Constants"


const KariUltrasonic = () => {
    const codeSnippet1 = `
kariUltrasonic(int trigPin, int echoPin);
float measure();
static void measureMulti();
int onMeasure(float lowerBound, float upperBound, void(*callback)(), bool triggerOnUpperBound=false);
void attach();
    `
    const codeSnippet2 = `
#include <kari.h>
#define TRIG_PIN 8
#define ECHO_PIN 7
using namespace kari;

void callback1();
void callback2();
void wrapper();

kariUltrasonic *ultra1;

void setup(){
Serial.begin(9600);
ultra1 = new kariUltrasonic(TRIG_PIN, ECHO_PIN);
}


void loop(){
    kariAsync::execute(wrapper, 1000);
}

void wrapper(){

            Serial.println("Using kariAsync");
            Serial.println(kari::getDistance<kariUltrasonic>(ultra1));

            ultra1->measure();
            ultra1->onMeasure(10, 10, callback1);
            ultra1->onMeasure(50, 50, callback2, true);
            ultra1->onMeasure(10, 30,
            [](){
                Serial.println("Distance is between 20 and 30");
            }
            );
        }

void callback1(){
    Serial.println("Distance is below 10");
}

void callback2(){
    Serial.println("Distance is above 50");
}
    `

    const introduction: string = `The kariUltrasonic class is designed for handling ultrasonic distance measurements efficiently in Arduino-based projects. 
    It provides both single and multi-sensor measurement capabilities, along with callback-based event handling to trigger actions 
    when objects are within a specified distance range.\n
    This class is optimized for non-blocking operations, making it ideal for real-time applications where multiple sensors need to be managed concurrently.
    `
   
    return (
        <>
            <section>
                <h1>{emoji.getUnicode("compass")} kariUltrasonic</h1>
                <h2>Introduction</h2>
                <p className={`text-formatter ${fs}`}>{introduction}</p>
            </section>
            <section>
                <h2>{emoji.getUnicode("wrench")} Key Features</h2>
                <ul className={`${fs}`}>
                    <li><strong>Simple Distance Measurement </strong>-Get distance readings with a single function call.</li>
                    <li><strong>Multi-Sensor Support</strong>-Measure multiple sensors asynchronously.</li>
                    <li><strong>Event-Driven Callbacks </strong>– Trigger functions when an object is within a specific distance range.</li>
                    <li><strong> Optimized for Performance</strong>– Works smoothly in large-scale IoT applications.</li>
                </ul>
            </section>
            <h2>{emoji.getUnicode("hammer_and_wrench")} API Reference</h2>
            <CodeFormatter codeSnippet={codeSnippet1}/>
            <section>
                <h2>{emoji.getUnicode("memo")} Class Methods</h2>
                <article>
                    <h3>{emoji.getUnicode("one")} Constructor</h3>
                    <CodeFormatter 
                    codeSnippet="kariUltrasonic(int trigPin, int echoPin);"
                    />
                    <h3>Description</h3>
                    <ul className={`${fs}`}>
                        <li>Initializes an ultrasonic sensor using the specified <b>trigger pin</b> and <b>echo pin</b>.</li>
                        <li>This must be called before using any measurement functions.</li>
                    </ul>
                    <h3>{emoji.getUnicode("bulb")} Example Usage:</h3>
                    <CodeFormatter
                    codeSnippet="kariUltrasonic ultra(8, 7); // Trig on pin 8, Echo on pin 7"
                    />
                </article>
                <article>
                    <h3>{emoji.getUnicode("two")} Destructor</h3>
                    <CodeFormatter 
                    codeSnippet="~kariUltrasonic(); "
                    />
                    <h3>Description</h3>
                    <ul className={`${fs}`}>
                       <li>Automatically cleans up resources when the object is destroyed.</li>
                    </ul>
                </article>
            </section>

            <section>
                <h2>{emoji.getUnicode("straight_ruler")} Measuring Distance</h2>
                <article>
                    <h3>{emoji.getUnicode("three")} Measure Distance (Single Sensor)</h3>
                    <CodeFormatter
                    codeSnippet="float measure();"
                    />
                    <h3>Description:</h3>
                    <ul className={`${fs}`}>
                        
                        <li>Returns the distance to the nearest object in <b> centimeters (cm)</b>.</li>
                        <li>Should be called inside the loop() function for continuous measurement.</li>
                    </ul>
                    <h3>{emoji.getUnicode("bulb")} Example Usage:</h3>
                    <CodeFormatter
                        codeSnippet={
                        `float distance = ultra.measure();\nSerial.println(distance);`.trim()
                        } />
                </article>
                <CodeArticle
                intro="Measure Distance (Multiple Sensors)"
                index="four"
                codeSignature={`static void measureMulti();`}
                codeUsage={null}
                other={null}
                data = {[
                    "Measures distances for <strong>all ultrasonic sensors</strong> initialized in the program",
                    "Useful when managing <b>multiple sensors</b> in an IoT system."
                ]}
                />
            </section>
            <section>
                <h2>{emoji.getUnicode("dart")} Event-Based Callbacks</h2>
                <CodeArticle
                    intro="Execute Function When Distance Is Within a Range"
                    index="five"
                    other={` The function <b class="green">alert()</b> will be called whenever an object is between 10 cm and 30 cm.`}
                    data={[
                        "Runs a callback function when the measured distance is between lowerBound and upperBound.",
                        "If triggerOnUpperBound = true, the callback runs when the distance exceeds upperBound."
                    ]}
                    codeSignature={`
                        int onMeasure(float lowerBound, float upperBound, void(*callback)(), bool triggerOnUpperBound = false);  
                        `
                    }
                    codeUsage={
                        `void alert() {\nSerial.println("Object detected in range!");\n}\n\nultra.onMeasure(10, 30, alert);
                        `}
                    />
            </section>
            <section>
                <h2>{emoji.getUnicode("link")} Multi-Sensor Integration</h2>
                <CodeArticle
                intro="Attach Sensor to Multi-Sensor System"
                index="six"
                codeSignature={`void attach();`}
                codeUsage={null}
                other={null}
                data={["Registers the sensor for multi-sensor measurements using measureMulti()."]}
                />
            </section>
            <h2>{emoji.getUnicode("hammer")} Basic Usage Example</h2>
            <CodeFormatter codeSnippet={codeSnippet2}/>
            
        </>
    )
}

export default KariUltrasonic
